import { CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { FaPlus, FaSave, FaTrash } from "react-icons/fa";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  deleteSuspicionRule,
  getPartnerSuspicionRules,
  savePartnerSuspicionRules,
} from "../../../../api/collaborator";
import { getPartners } from "../../../../api/partners";
import _ from "../../../../i18n";
import ConditionEditor from "../ConditionEditor";
import styles from "./RuleEditor.module.scss";

const groupColors = [
  "#18a0fb", // blue
  "#02af8e", // green
  "#d87318", // orange
  "#de4848", // red
  "#6d7f92", // gray
];

const RuleEditor = () => {
  const { token, navCommunity, spaceAs, loggedAs } = useSelector(
    (state) => state.auth
  );
  const [partner, setPartner] = useState(null);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState({
    conditions: [
      {
        type: "simple",
        variable: "realvsBilled",
        operator: ">",
        value: "",
      },
    ],
  });
  const [toRefresh, setToRefresh] = useState(false);

  useEffect(() => {
    if (toRefresh) {
      const timer = setTimeout(() => setToRefresh(false), 7000);
      return () => clearTimeout(timer);
    }
  }, [toRefresh]);

  const getPartnersQ = useQuery(
    ["partnersQuery", token, navCommunity],
    async () => {
      if (token && navCommunity) {
        const response = await getPartners({
          token,
          organizationId: navCommunity?.id,
        });
        const data = response.data.data.map((partner) => ({
          value: partner.id,
          label: partner.name,
          zone: partner.zone,
        }));
        setPartnerOptions(data);
        if (["ADMIN", "SUPERADMIN"].includes(loggedAs)) {
          if (data.length > 0) {
            const initPartner = data.filter(
              (partner) => partner.value == 25295
            );
            setPartner(initPartner[0]);
          }
        }
        return data;
      }
    },
    {
      enabled: !!token && !!navCommunity,
    }
  );

  const { isLoading, refetch } = useQuery(
    ["getSuspicionRules", token, navCommunity, partner],
    async () => {
      if (token && navCommunity && partner) {
        const response = await getPartnerSuspicionRules({
          token,
          organizationId: navCommunity?.id,
          partnerId: partner.value,
        });
        const data = response.data.data;
        setRules(data);
        return data;
      }
    },
    {
      enabled: !!token && !!navCommunity && !!partner,
    }
  );

  const variables = [
    {
      value: "realvsBilled",
      label: "Déséquilibre entre réalisé et facturé",
      acronym: "RvB",
    },
    {
      value: "hour_realvsBudget",
      label: "Proportion réalisé horaire à date vs budget à date",
      acronym: "HRvB",
    },
    {
      value: "eco_realvsBudget",
      label: "Proportion réalisé économique à date vs budget à date",
      acronym: "ERvB",
    },
  ];

  const operators = [
    { value: ">", label: ">" },
    { value: ">=", label: ">=" },
    { value: "<", label: "<" },
    { value: "<=", label: "<=" },
    { value: "==", label: "==" },
    { value: "!=", label: "!=" },
  ];

  const logicalOperators = [
    { value: "&&", label: "ET" },
    { value: "||", label: "OÙ" },
  ];

  const formatCondition = (condition) => {
    const getVariableLabel = (varValue) => {
      const variable = variables.find((v) => v.value === varValue);
      return variable?.acronym || variable?.label || varValue;
    };

    switch (condition.type) {
      case "range":
        return `${condition.minValue}% ${
          condition.minOperator
        } ${getVariableLabel(condition.variable)} ${condition.maxOperator} ${
          condition.maxValue
        }%`;

      case "nested": {
        const subs = condition.subConditions || [];
        if (subs.length === 0) return "";

        let result = "";
        let i = 0;

        while (i < subs.length) {
          const current = subs[i];
          const gid = current.groupId;

          if (gid) {
            const group = [];
            while (i < subs.length && subs[i].groupId === gid) {
              group.push(subs[i]);
              i++;
            }

            const groupStr = group
              .map((sub, idx) => {
                const str = formatCondition(sub);
                if (idx === 0) return str;
                return `${sub.logicalOp || "&&"} ${str}`;
              })
              .join(" ");

            result +=
              (result ? ` ${group[0].logicalOp || "&&"} ` : "") +
              `(${groupStr})`;
          } else {
            const str = formatCondition(current);
            result += (result ? ` ${current.logicalOp || "&&"} ` : "") + str;
            i++;
          }
        }

        return result;
      }

      case "simple":
      default:
        return `${getVariableLabel(condition.variable)} ${condition.operator} ${
          condition.value
        }%`;
    }
  };

  const formatRuleForBackend = (ruleData, ruleName = "Règle") => {
    if (!ruleData.conditions || ruleData.conditions.length === 0) {
      return `// ${ruleName}`;
    }

    let backendFormat = ``;

    ruleData.conditions.forEach((condition, index) => {
      if (index > 0) {
        backendFormat += "||\n";
      }
      const formattedCondition = formatCondition(condition);
      backendFormat += `${formattedCondition}\n`;
    });

    return backendFormat;
  };

  const validateCondition = (condition) => {
    switch (condition.type) {
      case "range":
        return (
          condition.variable &&
          condition.minValue !== "" &&
          condition.maxValue !== "" &&
          condition.minOperator &&
          condition.maxOperator
        );

      case "nested":
        return (
          condition.subConditions &&
          condition.subConditions.length > 0 &&
          condition.subConditions.every(validateCondition)
        );

      case "simple":
      default:
        return (
          condition.variable && condition.operator && condition.value !== ""
        );
    }
  };

  const isRuleValid = (rule) => {
    if (!rule.conditions || rule.conditions.length === 0) {
      return false;
    }
    return rule.conditions.every(validateCondition);
  };

  const updateCondition = (
    ruleData,
    setRuleData,
    index,
    field,
    value,
    parentIndex = null
  ) => {
    const newRuleData = { ...ruleData };

    if (parentIndex !== null) {
      if (!newRuleData.conditions[parentIndex].subConditions) {
        newRuleData.conditions[parentIndex].subConditions = [];
      }
      newRuleData.conditions[parentIndex].subConditions[index] = {
        ...newRuleData.conditions[parentIndex].subConditions[index],
        [field]: value,
      };
    } else {
      newRuleData.conditions[index] = {
        ...newRuleData.conditions[index],
        [field]: value,
      };
    }

    setRuleData(newRuleData);
  };

  const removeCondition = (
    ruleData,
    setRuleData,
    index,
    parentIndex = null
  ) => {
    const newRuleData = { ...ruleData };

    if (parentIndex !== null) {
      newRuleData.conditions[parentIndex].subConditions =
        newRuleData.conditions[parentIndex].subConditions.filter(
          (_, i) => i !== index
        );
    } else {
      newRuleData.conditions = newRuleData.conditions.filter(
        (_, i) => i !== index
      );
    }

    setRuleData(newRuleData);
  };

  const addCondition = (ruleData, setRuleData, parentIndex = null) => {
    const newCondition = {
      type: "simple",
      variable: "realvsBilled",
      operator: ">",
      value: "",
    };

    const newRuleData = { ...ruleData };

    if (parentIndex !== null) {
      if (!newRuleData.conditions[parentIndex].subConditions) {
        newRuleData.conditions[parentIndex].subConditions = [];
      }
      newRuleData.conditions[parentIndex].subConditions.push(newCondition);
    } else {
      newRuleData.conditions.push(newCondition);
    }

    setRuleData(newRuleData);
  };

  const DEFAULT_RULE = {
    conditions: [
      {
        type: "simple",
        variable: "realvsBilled",
        operator: ">",
        value: "",
      },
    ],
  };

  const saveNewRule = async () => {
    if (!isRuleValid(newRule)) return;

    const { id: organizationId } = navCommunity || {};
    const { value: partnerId } = partner || {};

    try {
      const response = await savePartnerSuspicionRules({
        token,
        organizationId,
        partnerId,
        rule: formatRuleForBackend(newRule, ""),
      });

      const data = response.data;

      if (data["result"] === "nOK") {
        toast.error(_("error"));
        return;
      }

      toast.success(_("saveSuccess"));

      setRules([...rules, { ...newRule, id: Date.now() }]);
      setNewRule(DEFAULT_RULE);
      setToRefresh(true);
      setSelected([]);
    } catch (error) {
      console.error("Error saving rule:", error);
      toast.error(_(error));
    }
  };

  const deleteRule = async (id) => {
    const { id: organizationId } = navCommunity || {};
    try {
      const response = await deleteSuspicionRule({
        token,
        organizationId,
        ruleId: id,
      });

      const data = response.data;

      if (data["result"] === "nOK") {
        toast.error(_("error"));
        return;
      }

      toast.success(_("deleteSuccess"));

      setRules(rules.filter((rule) => rule.id !== id));
    } catch (error) {
      console.error("Error deleting rule:", error);
      toast.error(_(error));
    }
  };

  const addSimpleCondition = () => {
    setNewRule({
      // ...newRule,
      conditions: [
        // ...newRule.conditions,
        {
          type: "simple",
          variable: "realvsBilled",
          operator: ">",
          value: "",
        },
      ],
    });
  };

  const addRangeCondition = () => {
    setNewRule({
      // ...newRule,
      conditions: [
        // ...newRule.conditions,
        {
          type: "range",
          variable: "realvsBilled",
          minValue: "",
          maxValue: "",
          minOperator: "<",
          maxOperator: "<=",
        },
      ],
    });
  };

  const addNestedCondition = () => {
    setNewRule({
      // ...newRule,
      conditions: [
        // ...newRule.conditions,
        {
          type: "nested",
          subConditions: [
            {
              type: "simple",
              variable: "realvsBilled",
              operator: ">",
              value: "",
            },
            {
              type: "simple",
              variable: "hour_realvsBudget",
              operator: ">",
              value: "",
            },
            {
              type: "simple",
              variable: "eco_realvsBudget",
              operator: ">",
              value: "",
            },
          ],
        },
      ],
    });
  };

  const handlePartnerChange = (partner) => {
    setPartner(partner);
  };

  const handleGroupConditions = () => {
    const groupId = Math.random().toString(36).slice(2, 9);
    const color = groupColors[Math.floor(Math.random() * groupColors.length)];

    setNewRule((prevRule) => ({
      ...prevRule,
      conditions: prevRule.conditions.map((cond) => {
        if (cond.type === "nested" && Array.isArray(cond.subConditions)) {
          return {
            ...cond,
            subConditions: cond.subConditions.map((sc, idx) =>
              selected.includes(idx) ? { ...sc, groupId, color } : sc
            ),
          };
        }
        return cond;
      }),
    }));

    setSelected([]);
  };

  return (
    <div className={styles["container"]}>
      <div className={styles["variables-box"]}>
        <h3 className={styles["variables-title"]}>Variables:</h3>
        <div className={styles["variables-list"]}>
          {variables.map((variable) => (
            <span key={variable.value} className={styles["variable-tag"]}>
              {variable.acronym} ({variable.label})
            </span>
          ))}
        </div>
      </div>

      <div className={`${styles["help-box"]} ${styles["variables-box"]}`}>
        <h3 className={styles["help-title"]}>Exemples:</h3>
        <div className={styles["help-content"]}>
          <div>
            <strong>Simple:</strong> RvB &gt; 20%
          </div>
          <div>
            <strong>Plage:</strong> 5% &lt; RvB ≤ 10%
          </div>
          <div>
            <strong>Imbriqué:</strong> (0% &lt; RvB ≤ 5%) AND (HRvB ≥ 10%) AND
            (ERvB ≥ 10%)
          </div>
        </div>
      </div>

      {toRefresh && (
        <div className={styles["tooltip"]}>
          <p>{_("newChangesDetected")}</p>
          <button
            onClick={() => {
              refetch();
              setToRefresh(false);
            }}
          >
            <ReloadOutlined size={14} width={14} /> {_("refresh")}
          </button>
        </div>
      )}

      <div className={styles["rules-section"]}>
        <div className={styles.container_header_filter}>
          <h2 className={styles["section-title"]}>Règles existantes</h2>
          {["SUPERADMIN", "ADMIN"].includes(loggedAs) &&
            ["SUPERADMIN", "ADMIN"].includes(spaceAs) && (
              <Select
                isSearchable
                closeMenuOnSelect
                placeholder="Partner"
                options={partnerOptions}
                value={partner}
                onChange={(inputValue) => handlePartnerChange(inputValue)}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    border: "1px solid #d9d9d9",
                    backgroundColor: "transparent",
                  }),
                  menu: (baseStyles, state) => ({
                    ...baseStyles,
                    zIndex: 2,
                  }),
                }}
              />
            )}
        </div>
        <div className={styles["rules-list"]}>
          {isLoading ? (
            "Loading..."
          ) : rules.length ? (
            rules.map((rule) => (
              <div key={rule.id} className={styles["rule-card"]}>
                <div className={styles["rule-card__inner"]}>
                  <div className={styles["rule-header"]}>
                    <div
                      className={styles["rule-card-number"]}
                    >{`#${partner.value}-${rule.id}`}</div>
                    <button
                      onClick={() => deleteRule(rule.id)}
                      className={styles["delete-button"]}
                    >
                      <FaTrash size={16} />
                    </button>
                  </div>
                  <div className="flex-1">
                    <pre className={styles["rule-code"]}>
                      {formatRuleForBackend(rule, rule.name)}
                    </pre>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <em>Aucune règle trouvé !</em>
          )}
        </div>
      </div>

      <div className={styles["new-rule-section"]}>
        <div className={styles["new-rule-box"]}>
          <h3 className={styles["new-rule-title"]}>{_("newRule")}</h3>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label className={styles["label"]}>
              Conditions
              {selected.length > 1 ? (
                <button
                  onClick={handleGroupConditions}
                >{`Grouper (${selected.length})`}</button>
              ) : null}
            </label>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {newRule.conditions.map((condition, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  {index > 0 && (
                    <div className={styles["or-label"]}>
                      <span>OR</span>
                    </div>
                  )}
                  <ConditionEditor
                    condition={condition}
                    index={index}
                    ruleData={newRule}
                    setRuleData={setNewRule}
                    variables={variables}
                    operators={operators}
                    logicalOperators={logicalOperators}
                    updateCondition={updateCondition}
                    removeCondition={removeCondition}
                    addCondition={addCondition}
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
              ))}
            </div>

            <div className={styles["add-condition-buttons"]}>
              <button
                onClick={addSimpleCondition}
                className={styles["btn-blue"]}
                title="Add simple condition (variable > value)"
              >
                <FaPlus size={14} />
                Simple
              </button>

              <button
                onClick={addRangeCondition}
                className={styles["btn-green"]}
                title="Add range condition (min < variable <= max)"
              >
                <FaPlus size={14} />
                Plage
              </button>

              <button
                onClick={addNestedCondition}
                className={styles["btn-purple"]}
                title="Add nested condition with sub-conditions"
              >
                <FaPlus size={14} />
                Imbriqué
              </button>
            </div>
          </div>

          <div className={styles["footer-note"]}>
            <button
              onClick={() =>
                setNewRule({
                  conditions: [
                    {
                      type: "simple",
                      variable: "realvsBilled",
                      operator: ">",
                      value: "",
                    },
                  ],
                })
              }
              className={styles["btn-cancel"]}
            >
              <CloseOutlined size={16} />
              {_("cancel")}
            </button>
            <button
              onClick={saveNewRule}
              disabled={!isRuleValid(newRule)}
              className={styles["btn-save"]}
            >
              <FaSave size={16} />
              {_("save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleEditor;
